import React, { useMemo, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import CreateExternalUserForm from '../../Components/CreateExternalUserForm';
import TenantPickerModal from '../Tenants/TenantPickerModal';
import { ExternalUser } from '../../types/guestAccess';
import { DATE_ONLY_RE } from '../../Helpers/leaseUtils';
import { isAcceptedReceiptFile } from '../../Helpers/keyRegisterUtils';
import {
    KeyHandOutInput,
    KeyHolderKind,
    KeyType,
    KEY_HOLDER_KINDS,
    KEY_NOTE_MAX_LENGTH,
    RegisterKey,
} from '../../types/keyRegister';
import KeyHolderPicker from './KeyHolderPicker';
import HandOutKeyList from './HandOutKeyList';
import { handOutDateLimit, hasKeysToHandOut } from './handOutKeys';
import { keySerialLabel } from './keyRegisterLabels';

interface HandOutKeysModalProps {
    show: boolean;
    onHide: () => void;
    types: KeyType[];
    keysByType: { [typeKey: string]: RegisterKey[] };
    /** YYYY-MM-DD (todayStockholm): the preset and latest allowed date. */
    today: string;
    onSubmit: (keyKeys: string[], input: KeyHandOutInput) => Promise<void>;
    submitting: boolean;
}

interface HandOutFormValues {
    keyKeys: string[];
    holderKind: KeyHolderKind;
    holderKey: string;
    effectiveDate: string;
    dueDate: string;
    receiptFile: File | null;
    note: string;
}

type Step = 'form' | 'create-external';

/**
 * "Lämna ut nycklar": several in-stock keys to one holder, with one Utlämnad den, Åter senast, note
 * and receipt for all of them. The date may not be before the latest earliest-date of the picked keys
 * (`handOutDateLimit`); the error names the key that sets it.
 */
const HandOutKeysModal: React.FC<HandOutKeysModalProps> = ({ show, onHide, types, keysByType, today, onSubmit, submitting }) => {
    const [step, setStep] = useState<Step>('form');
    const [showTenantPicker, setShowTenantPicker] = useState(false);
    const [externalUser, setExternalUser] = useState<ExternalUser | null>(null);
    const [fileInputKey, setFileInputKey] = useState(0);

    const keysByKey = useMemo(() => {
        const byKey: { [keyKey: string]: RegisterKey } = {};
        for (const keys of Object.values(keysByType)) for (const key of keys) byKey[key.key] = key;
        return byKey;
    }, [keysByType]);
    const typeNames = useMemo(() => Object.fromEntries(types.map(t => [t.key, t.name])), [types]);
    const anyInStock = useMemo(() => hasKeysToHandOut(types, keysByType), [types, keysByType]);

    const limitOf = (keyKeys: string[]) =>
        handOutDateLimit(keyKeys.map(k => keysByKey[k]).filter((k): k is RegisterKey => !!k), today);

    const schema = Yup.object({
        keyKeys: Yup.array(Yup.string().required()).min(1, 'Välj minst en nyckel'),
        holderKind: Yup.mixed<KeyHolderKind>().oneOf(KEY_HOLDER_KINDS).required(),
        holderKey: Yup.string().required('Välj mottagare'),
        effectiveDate: Yup.string()
            .required('Välj datum')
            .matches(DATE_ONLY_RE, 'Ogiltigt datum')
            .test('not-future', 'Datumet kan inte vara i framtiden', value => !value || value <= today)
            .test('after-previous', '', function (value) {
                const { min, blockingKey } = limitOf(this.parent.keyKeys ?? []);
                if (!value || !min || !blockingKey || value >= min) return true;
                const label = `${typeNames[blockingKey.typeKey] ?? 'Nyckel'}, ${keySerialLabel(blockingKey).toLowerCase()}`;
                return this.createError({ message: `Datumet kan inte vara före förra händelsen för ${label} (${min})` });
            }),
        dueDate: Yup.string()
            .matches(DATE_ONLY_RE, { message: 'Ogiltigt datum', excludeEmptyString: true })
            .test('after-hand-out', 'Kan inte vara före utlämningsdatumet', function (value) {
                const handedOut: string | undefined = this.parent.effectiveDate;
                return !value || !handedOut || value >= handedOut;
            }),
        receiptFile: Yup.mixed<File>()
            .nullable()
            .test('receipt-type', 'Kvittot måste vara en bild eller en PDF', file => !file || isAcceptedReceiptFile(file)),
        note: Yup.string().trim().max(KEY_NOTE_MAX_LENGTH, `Max ${KEY_NOTE_MAX_LENGTH} tecken`),
    });

    const close = () => {
        setStep('form');
        setExternalUser(null);
        setShowTenantPicker(false);
        onHide();
    };

    const handleSubmit = async (values: HandOutFormValues, helpers: FormikHelpers<HandOutFormValues>) => {
        helpers.setStatus(undefined);
        try {
            await onSubmit(values.keyKeys, {
                holder: { kind: values.holderKind, key: values.holderKey },
                effectiveDate: values.effectiveDate,
                dueDate: values.dueDate || undefined,
                note: values.note.trim() || undefined,
                ...(values.receiptFile ? { receiptFile: values.receiptFile, receiptKind: 'scan' as const } : {}),
            });
            setExternalUser(null);
        } catch (err) {
            helpers.setStatus(err instanceof Error ? err.message : 'Kunde inte lämna ut nycklarna');
        }
    };

    return (
        <Modal show={show} onHide={close} centered>
            <Modal.Header closeButton>
                <Modal.Title>{step === 'create-external' ? 'Ny extern användare' : 'Lämna ut nycklar'}</Modal.Title>
            </Modal.Header>
            {!anyInStock ? (
                <>
                    <Modal.Body>
                        <p className="mb-0">Inga nycklar inne att lämna ut.</p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={close}>Stäng</Button>
                    </Modal.Footer>
                </>
            ) : (
                <Formik<HandOutFormValues>
                    initialValues={{ keyKeys: [], holderKind: 'tenant', holderKey: '', effectiveDate: today, dueDate: '', receiptFile: null, note: '' }}
                    validationSchema={schema}
                    onSubmit={handleSubmit}
                >
                    {({ values, errors, touched, status, submitCount, handleChange, handleBlur, handleSubmit: submitForm, setStatus, setFieldValue, setFieldTouched }) => {
                        const pickHolder = (key: string) => {
                            void setFieldValue('holderKey', key);
                            void setFieldTouched('holderKey', false, false);
                        };
                        const switchKind = (kind: KeyHolderKind) => {
                            void setFieldValue('holderKind', kind);
                            pickHolder('');
                            setExternalUser(null);
                        };
                        const { min } = limitOf(values.keyKeys);
                        // The date's error can come from a key picked after the date was set, so show it once touched or submitted.
                        const dateError = (touched.effectiveDate || submitCount > 0) && errors.effectiveDate;

                        if (step === 'create-external') {
                            return (
                                <CreateExternalUserForm
                                    onUserCreated={user => {
                                        setExternalUser(user);
                                        pickHolder(user.uid);
                                        setStep('form');
                                    }}
                                    onCancel={() => setStep('form')}
                                />
                            );
                        }

                        return (
                            <>
                                <Form noValidate onSubmit={submitForm}>
                                    <Modal.Body>
                                        {status && <Alert variant="danger" dismissible onClose={() => setStatus(undefined)}>{status}</Alert>}

                                        <KeyHolderPicker
                                            kind={values.holderKind}
                                            holderKey={values.holderKey}
                                            error={(touched.holderKey && errors.holderKey) || undefined}
                                            externalUser={externalUser}
                                            onKindChange={switchKind}
                                            onPick={pickHolder}
                                            onPickExternal={user => {
                                                setExternalUser(user);
                                                pickHolder(user.uid);
                                            }}
                                            onCreateExternal={() => setStep('create-external')}
                                            onChooseTenant={() => setShowTenantPicker(true)}
                                        />

                                        <HandOutKeyList
                                            types={types}
                                            keysByType={keysByType}
                                            selected={values.keyKeys}
                                            onChange={keyKeys => {
                                                void setFieldValue('keyKeys', keyKeys);
                                                void setFieldTouched('keyKeys', true, false);
                                            }}
                                            error={(submitCount > 0 && typeof errors.keyKeys === 'string' && errors.keyKeys) || undefined}
                                        />

                                        <Form.Group className="mb-3" controlId="hand-out-effective-date">
                                            <Form.Label>Utlämnad den</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="effectiveDate"
                                                value={values.effectiveDate}
                                                min={min}
                                                max={today}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                isInvalid={!!dateError}
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.effectiveDate}</Form.Control.Feedback>
                                            <Form.Text className="text-muted">Gäller alla valda nycklar. Ändra om de lämnades ut en tidigare dag.</Form.Text>
                                        </Form.Group>

                                        <Form.Group className="mb-3" controlId="hand-out-due-date">
                                            <Form.Label>Åter senast</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="dueDate"
                                                value={values.dueDate}
                                                min={values.effectiveDate || undefined}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                isInvalid={touched.dueDate && !!errors.dueDate}
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.dueDate}</Form.Control.Feedback>
                                            <Form.Text className="text-muted">Valfritt. Med datum blir nycklarna utlånade och kan bli försenade.</Form.Text>
                                        </Form.Group>

                                        <Form.Group className="mb-3" controlId="hand-out-receipt">
                                            <Form.Label>Kvitto</Form.Label>
                                            <Form.Control
                                                key={fileInputKey}
                                                type="file"
                                                accept="image/*,application/pdf"
                                                onChange={e => {
                                                    const file = (e.target as HTMLInputElement).files?.[0] ?? null;
                                                    void setFieldValue('receiptFile', file);
                                                    void setFieldTouched('receiptFile', true, false);
                                                }}
                                                isInvalid={!!errors.receiptFile}
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.receiptFile as string}</Form.Control.Feedback>
                                            {values.receiptFile && (
                                                <Button variant="link" size="sm" className="px-0" onClick={() => {
                                                    void setFieldValue('receiptFile', null);
                                                    setFileInputKey(k => k + 1);
                                                }}>
                                                    Ta bort vald fil
                                                </Button>
                                            )}
                                            <Form.Text className="text-muted d-block">Valfritt. Inskannat kvitto som bild eller PDF; sparas på varje nyckel.</Form.Text>
                                        </Form.Group>

                                        <Form.Group controlId="hand-out-note">
                                            <Form.Label>Anteckning</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                name="note"
                                                value={values.note}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                isInvalid={touched.note && !!errors.note}
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.note}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <Button variant="secondary" onClick={close} disabled={submitting}>Avbryt</Button>
                                        <Button variant="primary" type="submit" disabled={submitting}>
                                            {submitting && <Spinner animation="border" size="sm" className="me-2" />}
                                            {values.keyKeys.length > 1 ? `Lämna ut ${values.keyKeys.length} nycklar` : 'Lämna ut'}
                                        </Button>
                                    </Modal.Footer>
                                </Form>
                                {/* Outside <Form>: React bubbles the picker's own submit through the portal to the enclosing form. */}
                                <TenantPickerModal
                                    show={showTenantPicker}
                                    onHide={() => setShowTenantPicker(false)}
                                    title="Välj mottagare"
                                    onPick={tenant => pickHolder(tenant.key)}
                                />
                            </>
                        );
                    }}
                </Formik>
            )}
        </Modal>
    );
};

export default HandOutKeysModal;
